import Vue from 'vue';
import Component from 'vue-class-component';
import KEYS from '@/values/keys';
import { CORE_ROUTES } from '@/core/core.router';
import { AUTH_EVENTS, PUBLIC_RATE_LIMIT_WAIT_SECONDS } from '@/auth/values/auth.values';
import { RegisterParams } from '@/auth/params/auth.params'
import EdRegisterProfileComponent from '@/auth/components/ed-register/ed-register-profile/ed-register-profile.component.vue';
import EdRegisterBasicInfoComponent from '@/auth/components/ed-register/ed-register-basic-info/ed-register-basic-info.component.vue';
import EdRegisterAccountInfoComponent from '@/auth/components/ed-register/ed-register-account-info/ed-register-account-info.component.vue';
import EdRegisterAgreementComponent from '@/auth/components/ed-register/ed-register-agreement/ed-register-agreement.component.vue';
import EdStepper from '@/components/common/ed-stepper/ed-stepper.vue';

@Component({
  name: 'register',
  components: {
    EdRegisterProfile: EdRegisterProfileComponent,
    EdRegisterBasicInfo: EdRegisterBasicInfoComponent,
    EdRegisterAccountInfo: EdRegisterAccountInfoComponent,
    EdRegisterAgreement: EdRegisterAgreementComponent,
    EdStepper
  },
})
export default class RegisterView extends Vue {

  /* Data */
  public step: number = 1;
  public userType: string = '';
  public basicInfo: any = null;
  public accountInfo: any = null;
  public termsId: number = null;
  public acceptTerms: boolean = false;
  public validationOnly: boolean = false;
  public registrationRateLimitSeconds: number = 0;
  private registrationRateLimitTimer: number = null;

  /* Methods */
  goHome() {
    this.$router.push({ name: CORE_ROUTES.HOME.NAME })
  }

  onStepChange({step, userType}) {
    this.step = step;
    this.userType = userType;
  }

  getFormBasicInfo(form) {
    this.basicInfo = form;
  }

  async getFormAccountInfo(form) {
    this.accountInfo = form;
    this.validateUser();
  }

  async confirmTerms({termsId, acceptTerms}) {
    this.termsId = termsId;
    this.acceptTerms = acceptTerms;
    this.validationOnly = false;
    this.createUser();
  }

  async createUser () {
    try {
      let data = await this.setFormData();
      await this.$Auth.Services.createUser(data);
      this.$EdModal.createMessageModal({
        body: this.$i18n.t('auth.register.info.registerConfirmation'),
        title: this.$i18n.t('auth.register.register'),
        type: KEYS.EVENT_TYPES.SUCCESS,
        action: () => {
          this.goHome()
        }
      });
    } catch {
      this.$EdModal.createMessageModal({
        body: this.$i18n.t('auth.register.errors.registerError'),
        title: this.$i18n.t('auth.register.register'),
        type: KEYS.EVENT_TYPES.WARNING
      });
    }
  }

  async validateUser () {
    if (this.registrationRateLimitSeconds > 0) {
      return;
    }

    this.validationOnly = true;
    try {
      let data = await this.setFormData();
      await this.$Auth.Services.createUser(data);
      this.$edEventBus.$emit(AUTH_EVENTS.ON_CHANGE_REGISTER_STEP, {step: 4, userType: this.userType})
    } catch(error) {
      if (error?.status === 429) {
        this.startRegistrationRateLimitThrottle();
        return;
      }

      let emailFail = false;
      let usernameFail = false;
      // Only handles email and username errors, the others are handled on the front
      (error.errors || []).forEach(el => {
        if(el.code == 'PS005') emailFail = true;
        if(el.code == 'PS004') usernameFail = true;
      });
      if(usernameFail) {
        this.$edEventBus.$emit(AUTH_EVENTS.RESET_ACCOUNT_INFO_FORM);
        this.$EdModal.createMessageModal({
          body: this.$i18n.t('auth.register.errors.username'),
          title: this.$i18n.t('auth.register.register'),
          type: KEYS.EVENT_TYPES.WARNING
        });
      } else if(emailFail) {
        this.$edEventBus.$emit(AUTH_EVENTS.RESET_ACCOUNT_INFO_FORM);
        this.$EdModal.createMessageModal({
          body: this.$i18n.t('auth.register.errors.email'),
          title: this.$i18n.t('auth.register.register'),
          type: KEYS.EVENT_TYPES.WARNING
        });
      } else this.$edEventBus.$emit(AUTH_EVENTS.ON_CHANGE_REGISTER_STEP, {step: 4, userType: this.userType})
    }
  }

  private startRegistrationRateLimitThrottle() {
    if (this.registrationRateLimitTimer !== null) {
      window.clearInterval(this.registrationRateLimitTimer);
    }

    this.registrationRateLimitSeconds = PUBLIC_RATE_LIMIT_WAIT_SECONDS.REGISTRATION;
    this.registrationRateLimitTimer = window.setInterval(() => {
      this.registrationRateLimitSeconds = Math.max(this.registrationRateLimitSeconds - 1, 0);
      if (this.registrationRateLimitSeconds === 0 && this.registrationRateLimitTimer !== null) {
        window.clearInterval(this.registrationRateLimitTimer);
        this.registrationRateLimitTimer = null;
      }
    }, 1000);
  }

  async setFormData() {
    let data: RegisterParams = {
      personFirstName: this.basicInfo.personFirstName.value,
      personMiddleName: this.basicInfo.personMiddleName.value ? this.basicInfo.personMiddleName.value : null,
      personLastName: this.basicInfo.personLastName.value,
      personAddress1: this.basicInfo.personAddress1.value,
      personAddress2: this.basicInfo.personAddress2.value,
      personAddress3: this.basicInfo.personAddress3.value,
      personPhoneNumber: this.basicInfo.personPhoneNumber.value,
      personPhoneNumberExt: this.basicInfo.personPhoneNumberExt.value ? this.basicInfo.personPhoneNumberExt.value : null,
      personCountryCode: this.basicInfo.personCountryCode.value,
      personZipCode: this.basicInfo.personZipCode.value,
      personState: this.basicInfo.personState.value,
      personCity: this.basicInfo.personCity.value,
      personRepresentOrganization: this.basicInfo.personRepresentOrganization.value,
      personPhoneVerificationCode: this.basicInfo.personPhoneVerificationCode?.value,
      personPhoneVerificationId: this.basicInfo.personPhoneVerificationId?.value,
      personInterestCategoryIdList: this.basicInfo.personInterestCategoryIdList?.value  ? this.basicInfo.personInterestCategoryIdList.value : [],
      organizationName: this.basicInfo.organizationName?.value,
      organizationTypeId: this.basicInfo.organizationTypeId?.value,
      organizationRegisteredEnterprise: this.basicInfo.organizationRegisteredEnterprise?.value,
      organizationEIN: this.basicInfo.organizationEIN?.value,
      organizationCategoryIdList: this.basicInfo.organizationCategoryIdList.value ? this.basicInfo.organizationCategoryIdList.value : [],
      organizationWebsite: this.basicInfo.organizationWebsite?.value,
      organizationPhoneNumber: this.basicInfo.organizationPhoneNumber?.value,
      organizationPhoneNumberExt: this.basicInfo.organizationPhoneNumberExt?.value,
      organizationSameAddressAsApplicant: this.basicInfo.organizationSameAddressAsApplicant.value,
      organizationAddress1: this.basicInfo.organizationAddress1?.value,
      organizationAddress2: this.basicInfo.organizationAddress2?.value,
      organizationAddress3: this.basicInfo.organizationAddress3?.value,
      organizationCountryCode: this.basicInfo.organizationCountryCode?.value,
      organizationZipCode: this.basicInfo.organizationZipCode?.value,
      organizationState: this.basicInfo.organizationState?.value,
      organizationCity: this.basicInfo.organizationCity?.value,
      username: this.accountInfo.username.value,
      email: this.accountInfo.email.value,
      password: this.accountInfo.password.value,
      passwordConfirmation: this.accountInfo.passwordConfirmation.value,
      tncAccepted: this.acceptTerms,
      termsAndConditionsId: this.termsId,
      requestType: this.userType,
      validationOnly: this.validationOnly
    }
    return data;
  }

  /* Hooks */
  mounted() {
    this.$edEventBus.$on(AUTH_EVENTS.ON_CHANGE_REGISTER_STEP, this.onStepChange)
  }

  destroyed() {
    this.$edEventBus.$off(AUTH_EVENTS.ON_CHANGE_REGISTER_STEP, this.onStepChange)
    if (this.registrationRateLimitTimer !== null) {
      window.clearInterval(this.registrationRateLimitTimer);
    }
  }

}
